import React, { useEffect, useRef, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

// --- CATEGORY COLORS ---
const CATEGORY_COLORS = {
  '[PERSONAL]': 'var(--green)',
  '[AWARD]': '#6DC3BB',    // Yellow/Gold
  '[RESEARCH]': '#5459AC', // Light Blue
  '[SERVICE]': '#f38affff', // Light Blue
  '[TALK]': 'rgb(101, 101, 101)', // Light Blue
  'DEFAULT': 'var(--green)'
};

const getCategoryColor = (type) => {
  if (!type) return CATEGORY_COLORS.DEFAULT;
  const upperType = type.toUpperCase().trim();
  return CATEGORY_COLORS[upperType] || CATEGORY_COLORS.DEFAULT;
};

const StyledNewsSection = styled.section`
  max-width: 1080px;
  .inner { display: block; }
`;

const YearGroup = styled.div`
  border-left: 2px solid var(--lightest-navy);
  padding-left: 20px;
  margin-bottom: 24px;
`;

const YearHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 0;
  background: transparent;
  border: 0;
  color: var(--light-slate);
  font-family: var(--font-mono);
  font-size: var(--fz-sm);
  text-align: left;
  cursor: pointer;

  .year-label {
    font-weight: 600;
    color: var(--slate);
  }
  .toggle-icon {
    font-size: var(--fz-md);
    transition: transform 0.15s ease-out;
  }
`;

const NewsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
`;

const NewsItem = styled.li`
  position: relative;
  margin-bottom: 10px;
  padding-left: 20px;
  font-size: var(--fz-sm);

  &:before {
    content: '•';
    position: absolute;
    left: 0;
    top: 3px;
    color: var(--green);
    font-size: var(--fz-sm);
  }

  .meta {
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    color: var(--light-slate);
    margin-bottom: 2px;
  }
  .title {
    font-weight: 500;
    color: var(--slate);
  }
  .type {
    margin-right: 4px;
    font-weight: 600;
  }
  .body {
    margin-top: 2px;
    font-size: var(--fz-xs);
    color: var(--slate);
  }
  a {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }
  a:hover {
    text-decoration-color: var(--green);
  }
`;

const News = () => {
  // NOTE: If your files are not in "content/news", remove the filter line below.
  const data = useStaticQuery(graphql`
    query {
      news: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/news/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              date
              type
              url
            }
            html
          }
        }
      }
    }
  `);

  const newsEdges = data.news?.edges || [];

  // --- DEBUGGING: Check your browser console (F12) to see if news was found ---
  console.log("News Data Found:", newsEdges); 

  // Group news items by year
  const newsByYear = newsEdges.reduce((acc, { node }) => {
    const { date } = node.frontmatter;
    let year = 'Unknown';

    // Robust date handling (handles both String and Date object)
    if (date) {
      const dateStr = date.toString(); 
      if (dateStr.length >= 4) {
        year = dateStr.slice(0, 4);
      }
    }

    if (!acc[year]) acc[year] = [];
    acc[year].push(node);
    return acc;
  }, {});

  const years = Object.keys(newsByYear)
    .filter(y => !!y)
    .sort((a, b) => {
      // Handle 'Unknown' or non-numbers gracefully
      if (isNaN(a)) return 1;
      if (isNaN(b)) return -1;
      return Number(b) - Number(a); 
    });

  const [openYears, setOpenYears] = useState(() => {
    if (years.length === 0) return {};
    return { [years[0]]: true };
  });

  const toggleYear = year => {
    setOpenYears(prev => ({
      ...prev,
      [year]: !prev[year],
    }));
  };

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (revealContainer.current) {
      sr.reveal(revealContainer.current, srConfig());
    }
  }, [prefersReducedMotion]);

  return (
    <StyledNewsSection id="news" ref={revealContainer}>
      <h2 className="numbered-heading">News</h2>

      <div className="inner">
        {years.length === 0 && <p>No news found. Check console for details.</p>}

        {years.map(year => {
          const isOpen = !!openYears[year];

          return (
            <YearGroup key={year}>
              <YearHeader
                type="button"
                onClick={() => toggleYear(year)}
                aria-expanded={isOpen}
                aria-controls={`news-year-${year}`}>
                <span className="year-label">{year}</span>
                <span
                  className="toggle-icon"
                  style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  ▶
                </span>
              </YearHeader>

              {isOpen && (
                <div id={`news-year-${year}`}>
                  <NewsList>
                    {newsByYear[year].map((node, idx) => {
                      const { frontmatter, html } = node;
                      const { title, date, type, url } = frontmatter;
                      // Ensure date is a string for display
                      const displayDate = date ? date.toString().slice(0, 10) : '';

                      return (
                        <NewsItem key={`${year}-${idx}`}>
                          {displayDate && <div className="meta">{displayDate}</div>}
                          <div className="title">
                            {type && (
                              <span 
                                className="type" 
                                style={{ color: getCategoryColor(type) }}
                              >
                                {type}
                              </span>
                            )}
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-link">
                                {title}
                              </a>
                            ) : (
                              title
                            )}
                          </div>
                          {html && (
                            <div
                              className="body"
                              dangerouslySetInnerHTML={{ __html: html }}
                            />
                          )}
                        </NewsItem>
                      );
                    })}
                  </NewsList>
                </div>
              )}
            </YearGroup>
          );
        })}
      </div>
    </StyledNewsSection>
  );
};

export default News;